import { BehaviorSubject, Observable } from 'rxjs';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize } from 'rxjs/operators';

const API_PING_ENDPOINT = '/api/v1/ping/anonymous';
const API_PING_TIMEOUT_MS = 15_000;

export enum ConnectivityStatus {
  Online,
  BrowserOffline,
  ApiUnavailable
}

@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  public connectivityStatus$: Observable<ConnectivityStatus>;

  private apiBaseUrl = '';
  private apiReachable = navigator.onLine;
  private browserOnline = navigator.onLine;
  private connectivityStatus = new BehaviorSubject<ConnectivityStatus>(this.getCurrentStatus());
  private http: HttpClient;
  private pingInProgress = false;

  constructor(httpBackend: HttpBackend) {
    // HttpBackend will bypass interceptors so the connectivity check cannot intercept itself.
    this.http = new HttpClient(httpBackend);
    this.connectivityStatus$ = this.connectivityStatus.asObservable();
  }

  public setApiBaseUrl(apiBaseUrl: string): void {
    this.apiBaseUrl = apiBaseUrl?.replace(/\/+$/, '') ?? '';
  }

  public verifyApiConnection(): void {
    if (!this.browserOnline || this.apiBaseUrl.length === 0 || this.pingInProgress) {
      return;
    }

    this.pingInProgress = true;
    this.http
      .get(`${this.apiBaseUrl}${API_PING_ENDPOINT}`, { timeout: API_PING_TIMEOUT_MS })
      .pipe(finalize(() => (this.pingInProgress = false)))
      .subscribe({
        next: () => this.reportApiReachable(),
        error: () => this.reportApiUnavailable()
      });
  }

  public isApiRequest(url: string): boolean {
    return (
      url?.startsWith('/api/') ||
      (this.apiBaseUrl.length > 0 &&
        (url === this.apiBaseUrl || url.startsWith(`${this.apiBaseUrl}/`)))
    );
  }

  public setBrowserOnline(browserOnline: boolean): void {
    this.browserOnline = browserOnline;
    if (!browserOnline) {
      this.apiReachable = false;
    }
    this.publishCurrentStatus();
  }

  public reportApiReachable(): void {
    this.apiReachable = true;
    this.publishCurrentStatus();
  }

  public reportApiUnavailable(): void {
    this.apiReachable = false;
    this.publishCurrentStatus();
  }

  private getCurrentStatus(): ConnectivityStatus {
    if (!this.browserOnline) {
      return ConnectivityStatus.BrowserOffline;
    }

    return this.apiReachable ? ConnectivityStatus.Online : ConnectivityStatus.ApiUnavailable;
  }

  private publishCurrentStatus(): void {
    const status = this.getCurrentStatus();
    if (this.connectivityStatus.value !== status) {
      this.connectivityStatus.next(status);
    }
  }
}
